import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { JIRA_AGENT_POOL_SERVER } from "./accesses.ts";
import { JiraAiIconUrl } from "./icon.ts";
import { JiraAiSiteWideTools, grantableJiraAiTools } from "./tools.ts";

/** The resolver shape of agentic's `McpServerSpecResolver`, restated here:
 * jira-ai-shared can't import agentic-shared, which imports it for the MCP
 * registry. */
type JiraAiMcpServerSpecResolver = (location: {
  gateway: string;
  server: string;
}) => McpServerSpec & { type: "external" };

// The API key works at once. The wait is for the account's new project role
// to take effect in Jira: about 12 seconds in the live checks, and once 74
// seconds.
const jiraAiPropagationHint = propagationHint({
  maxWait: "2 minutes",
  normalWait: "10 to 20 seconds",
  pollInterval: "5 seconds",
  resource: "Jira",
});

const accessPrompt = `Request access to Jira tools in one project by calling with argv:

  [<site>, <tool>..., "--project", <project>, "--duration", "1h", "--reason", <reason>]

or, for a team-managed project:

  [<site>, <tool>..., "--project", <project>, "--role", <role>, "--duration", "1h", "--reason", <reason>]

or, for site-wide tools only, with no project:

  [<site>, <tool>..., "--duration", "1h", "--reason", <reason>]

where:

  - site: The Jira site, by its identifier in P0. Use the list tool to
    enumerate them: each lists its cloudId.
  - tool: One or more Jira tools you intend to call, and no others. Use the
    list tool to enumerate them.
  - project: The key of a Jira project (Jira calls it a space), such as ENG.
    These tools are site-wide and need no project: ${JiraAiSiteWideTools.join(", ")}.
    So to find a project's key, request listJiraProjects without a project
    first. Every other tool needs a project, even in a request that also has
    site-wide tools.
  - role: For a team-managed project only, and required there: the name of
    one of the project's roles, such as Member. Leave it out for a
    company-managed project, where P0 picks the role from the tools. If you
    don't know which kind the project is, request without it: P0's answer
    says whether the project needs it, and lists the project's roles.
  - duration: Always "1h". P0 refuses Jira access longer than 60 minutes,
    and without it the request gets your organization's default duration,
    which is usually longer.
  - reason: The reason access is needed

On every tool call, pass the request ID of the grant that covers it, and the
site's cloudId as the tool's cloudId argument. Take the cloudId from the site
list, even where a tool's description says to call
getAccessibleAtlassianResources: P0 doesn't grant that tool. Each grant is
for one project, or for site-wide tools only, and your session holds one Jira
grant at a time: to work in another project, wait for the grant to expire or
revoke it, then request again.

Access can take a minute or two to start after approval. If a call fails with "not found" or "does not exist" soon after approval, wait a few seconds and retry, for up to two minutes, before deciding the item doesn't exist.

Access lasts at most 60 minutes and can't be extended. When it ends, or is revoked, calls fail with 'Access not granted' or an error from Jira. Request access again with the access tool and poll check with the new request id. Calling check on the old request id does not renew it.
`;

/** "Jira (agent pool)": Atlassian's MCP server, called with the leased agent
 * account's API key, which each grant copies into its request's own secret.
 *
 * One server serves every installed site: a resolver sees only the gateway
 * and server, and the gateway can't pin tool arguments, so the agent passes
 * the site's `cloudId` itself. The gateway lists upstream tools only for an
 * OAuth credential, so the tools are the static table less every tool P0
 * never grants.
 */
export const jiraAiMcpResolver: JiraAiMcpServerSpecResolver = (location) => {
  const prefixArgs = ["jira-ai", "tool", location.gateway, location.server];
  // `ls jira-ai tool` takes each listable parameter as a subcommand and the
  // others only as options, so the gateway and server can't come first here.
  const listPrefixArgs = ["jira-ai", "tool"];
  return {
    access: {
      prefixArgs,
      prompt: accessPrompt,
      propagationHint: jiraAiPropagationHint,
    },
    // The gateway checks each call against the request's per-tool
    // entitlements, and Jira checks the project role.
    authorization: "tool",
    credential: {
      // Each grant stages its own secret, which the gateway reads from the
      // vault project the grant names in `generated.vault`.
      path: "{{ request.generated.secretName }}",
      type: "vault",
    },
    iconUrl: JiraAiIconUrl,
    label: "Jira (agent pool)",
    list: {
      fields: {
        site: { description: "Jira site, by its P0 identifier", required: [] },
        tool: {
          description: "Jira tool an agent can be granted",
          required: [],
        },
      },
      prefixArgs: listPrefixArgs,
    },
    tools: { tools: grantableJiraAiTools, type: "manual" },
    type: "external",
    url: "https://mcp.atlassian.com/v2/mcp?tools=all",
  };
};

/** Whether {@link jiraAiMcpResolver} can be added: only once a Jira site is
 * installed, since without one there is nothing an agent could request. */
export const jiraAiMcpServerAvailability = {
  [JIRA_AGENT_POOL_SERVER]: (installed: Record<string, NewConfig>): boolean =>
    installedIds("site", { site: installed["jira-ai"]?.site ?? {} }).length > 0,
};
